import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    setItems,
    upsertItem,
    removeItem,
    clearAll,
    clearSurface,
    setLoading,
} from '../slices/searchHistorySlice';
import { refreshAndRetry } from '../utils/refreshAndRetry';

// API returns snake_case rows; normalize to camelCase at the boundary so the
// rest of the app only sees the shape it actually wants to render.
const normalize = (row) => ({
    id: row.id,
    entityType: row.entity_type,
    queryText: row.query_text,
    entityPublicId: row.entity_public_id,
    entityName: row.entity_name,
    entityArtwork: row.entity_artwork,
    entitySubtitle: row.entity_subtitle,
    searchedAt: row.searched_at,
    surface: row.surface || 'mixpedia',
});

export const fetchSearchHistory = createAsyncThunk(
    'searchHistory/fetch',
    async ({ surface = 'mixpedia', limit = 30 } = {}, { dispatch }) => {
        try {
            dispatch(setLoading(true));
            const result = await refreshAndRetry(
                (token) => window.electron.library.getSearchHistory(token, limit, surface)
            );
            if (result.error) return;
            const rows = (result.data || []).map(normalize);
            dispatch(setItems({ surface, rows }));
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const addSearchHistoryEntry = createAsyncThunk(
    'searchHistory/add',
    async (entry, { dispatch, getState }) => {
        const surface = entry.surface || 'mixpedia';
        // When adding a canonical entry (real publicId), purge any leftover
        // placeholder entry for the same entityType+name from local state and
        // the server so the recent-search list doesn't carry both side-by-side.
        // Placeholders use "name:..." (unknown-id artist/track from a tap) or
        // "q:..." (free-text Enter from Set Planner / Mixpedia tracks).
        const incomingPid = String(entry.entityPublicId || '');
        const isCanonical = entry.entityType !== 'query'
            && incomingPid
            && !incomingPid.startsWith('name:')
            && !incomingPid.startsWith('q:');
        if (isCanonical && entry.entityName) {
            const targetName = entry.entityName.toLowerCase();
            const targetSub = (entry.entitySubtitle || '').toLowerCase();
            const placeholders = (getState().searchHistory.items || []).filter(e =>
                (e.surface || 'mixpedia') === surface
                && e.entityType === entry.entityType
                && typeof e.entityPublicId === 'string'
                && (
                    // explicit placeholder rows: drop on name match
                    ((e.entityPublicId.startsWith('name:') || e.entityPublicId.startsWith('q:'))
                        && (e.entityName || '').toLowerCase() === targetName)
                    // sibling canonical rows for the same conceptual entity:
                    // drop on name+subtitle match (different publicId source)
                    || (!e.entityPublicId.startsWith('name:')
                        && !e.entityPublicId.startsWith('q:')
                        && (e.entityName || '').toLowerCase() === targetName
                        && (e.entitySubtitle || '').toLowerCase() === targetSub)
                )
            );
            for (const ph of placeholders) {
                dispatch(deleteSearchHistoryEntry(ph.id));
            }
        }

        // Optimistic: insert a tombstone-style row so the dropdown reflects it
        // immediately. The server-returned row will replace it on success.
        const optimistic = {
            id: `tmp-${Date.now()}`,
            entityType: entry.entityType,
            queryText: entry.queryText || null,
            entityPublicId: entry.entityPublicId || null,
            entityName: entry.entityName || null,
            entityArtwork: entry.entityArtwork || null,
            entitySubtitle: entry.entitySubtitle || null,
            searchedAt: new Date().toISOString(),
            surface,
        };
        dispatch(upsertItem(optimistic));

        const result = await refreshAndRetry(
            (token) => window.electron.library.addSearchHistory(token, { ...entry, surface })
        );
        if (result?.error || !result?.data) return;
        dispatch(upsertItem(normalize(result.data)));
    }
);

export const deleteSearchHistoryEntry = createAsyncThunk(
    'searchHistory/delete',
    async (id, { dispatch }) => {
        dispatch(removeItem(id));
        await refreshAndRetry(
            (token) => window.electron.library.deleteSearchHistoryItem(token, id)
        );
    }
);

export const clearSearchHistoryAll = createAsyncThunk(
    'searchHistory/clear',
    async (_, { dispatch }) => {
        dispatch(clearAll());
        await refreshAndRetry(
            (token) => window.electron.library.clearSearchHistory(token)
        );
    }
);

// Per-(surface, type) clear: server has no bulk-by-type endpoint, so we
// optimistically drop matching items from the slice and fan out per-id deletes
// in parallel.
export const clearSearchHistoryByType = createAsyncThunk(
    'searchHistory/clearByType',
    async ({ entityType, surface = 'mixpedia' }, { dispatch, getState }) => {
        const items = getState().searchHistory.items.filter(e =>
            e.entityType === entityType && (e.surface || 'mixpedia') === surface
        );
        items.forEach(e => dispatch(removeItem(e.id)));
        await Promise.all(items
            .filter(e => typeof e.id === 'number' || (typeof e.id === 'string' && !e.id.startsWith('tmp-')))
            .map(e => refreshAndRetry(
                (token) => window.electron.library.deleteSearchHistoryItem(token, e.id)
            ))
        );
    }
);

// Wipe an entire surface (used when a surface's recents UI offers a "clear all
// recents" affordance scoped to that surface).
export const clearSearchHistoryBySurface = createAsyncThunk(
    'searchHistory/clearSurface',
    async (surface, { dispatch }) => {
        dispatch(clearSurface(surface));
        await refreshAndRetry(
            (token) => window.electron.library.clearSearchHistory(token, surface)
        );
    }
);
